import { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/Header";
import { ProfileSidebarAnfitriao } from "@/components/ProfileSidebarAnfitriao";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { AnfitriaoDashboardData } from "@/types/anfitriao-dashboard";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clipboard,
  ExternalLink,
  Megaphone,
  Send,
  Trash2,
  UploadCloud,
} from "lucide-react";

type Creative = {
  id: number;
  title: string;
  source: string;
  status: string;
  statusLabel: string;
  fileUrl: string | null;
  isImage: boolean;
  duplicateUrl: string | null;
  deleteUrl: string | null;
  canDelete: boolean;
};

type PlannerItem = {
  id: number;
  campaignId: number | null;
  scheduledDate: string | null;
  dayLabel: string | null;
  channel: string | null;
  format: string | null;
  theme: string | null;
  caption: string | null;
  cta: string | null;
  status: string;
  approvalStatus: string;
  approvalLabel: string;
  published: boolean;
  markPublishedUrl: string;
  deleteUrl: string;
  creative: Creative | null;
};

type Campaign = {
  id: number;
  title: string;
  description: string | null;
  deleteUrl?: string | null;
};

type HostMarketingPayload = {
  hostName: string;
  campaigns: Campaign[];
  creatives: Creative[];
  plannerItems: PlannerItem[];
  nextPlannerItem: PlannerItem | null;
  stats: {
    activeCampaignTotal: number;
    pendingCount: number;
    publishedCount: number;
  };
  routes: {
    storeCreative: string;
    storePlannerItem: string;
  };
  dayOptions: string[];
};

function getCsrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
}

function joinedCaption(item: PlannerItem): string {
  return [item.caption, item.cta].filter(Boolean).join("\n\n");
}

function normalizeWeek(items: PlannerItem[]): Map<number, PlannerItem[]> {
  const withDates = items
    .filter((item) => item.scheduledDate)
    .sort((a, b) => String(a.scheduledDate).localeCompare(String(b.scheduledDate)));
  const base = withDates[0]?.scheduledDate ? new Date(`${withDates[0].scheduledDate}T00:00:00`) : null;
  const weeks = new Map<number, PlannerItem[]>();

  items.forEach((item) => {
    let week = 0;
    if (item.scheduledDate && base) {
      const current = new Date(`${item.scheduledDate}T00:00:00`);
      week = Math.floor((current.getTime() - base.getTime()) / (1000 * 60 * 60 * 24 * 7)) + 1;
    }
    if (!weeks.has(week)) weeks.set(week, []);
    weeks.get(week)!.push(item);
  });

  return new Map([...weeks.entries()].sort((a, b) => a[0] - b[0]));
}

function Pill({ children, tone = "default" }: { children: React.ReactNode; tone?: "default" | "green" | "coral" }) {
  const styles = {
    default: "bg-muted text-muted-foreground",
    green: "bg-emerald-50 text-emerald-700",
    coral: "bg-coral/10 text-coral",
  }[tone];

  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ${styles}`}>{children}</span>;
}

function Collapsible({
  title,
  count,
  defaultOpen = false,
  children,
  action,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card className="card-shadow border-0 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-4 transition-colors hover:bg-forest-light/60">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
        >
          <span className="min-w-0">
            <span className="block truncate text-base font-extrabold text-foreground">{title}</span>
            {typeof count === "number" && <span className="text-sm font-semibold text-muted-foreground">{count} itens</span>}
          </span>
          {open ? <ChevronUp className="h-5 w-5 shrink-0 text-coral" /> : <ChevronDown className="h-5 w-5 shrink-0 text-coral" />}
        </button>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {open && <CardContent className="border-t bg-white p-4">{children}</CardContent>}
    </Card>
  );
}

function DangerButton({
  label,
  confirmMessage,
  onConfirm,
}: {
  label: string;
  confirmMessage: string;
  onConfirm: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
      onClick={() => {
        if (window.confirm(confirmMessage)) {
          onConfirm();
        }
      }}
    >
      <Trash2 className="h-4 w-4" />
      {label}
    </Button>
  );
}

function ItemCard({
  item,
  onCopy,
  onPublished,
  onDelete,
  featured = false,
}: {
  item: PlannerItem;
  onCopy: (text: string) => void;
  onPublished: (item: PlannerItem) => void;
  onDelete: (url: string, message: string) => void;
  featured?: boolean;
}) {
  const text = joinedCaption(item);
  const hasImage = Boolean(item.creative?.fileUrl && item.creative.isImage);

  return (
    <Card className={`border bg-white shadow-sm ${featured ? "border-coral/30" : "border-border"}`}>
      <CardContent className="p-3 sm:p-4">
        <div className={`grid gap-4 ${hasImage ? "md:grid-cols-[220px_minmax(0,1fr)]" : "grid-cols-1"}`}>
          {hasImage && (
            <a
              href={item.creative!.fileUrl!}
              target="_blank"
              rel="noreferrer"
              className="block overflow-hidden rounded-2xl border bg-muted/20"
            >
              <img src={item.creative!.fileUrl!} alt={item.creative!.title} className="h-auto max-h-80 w-full object-contain" />
            </a>
          )}

          <div className="min-w-0 space-y-3">
            <div className="flex flex-wrap gap-2">
              {item.dayLabel && <Pill tone="coral">{item.dayLabel}</Pill>}
              <Pill tone={item.published ? "green" : "default"}>{item.published ? "Publicado" : "Pendente"}</Pill>
              <Pill>{item.approvalLabel}</Pill>
            </div>

            <div>
              <h3 className="text-lg font-extrabold leading-tight text-foreground">{item.theme || "Sem tema"}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {item.channel || "Sem canal"} · {item.format || "Sem formato"}
              </p>
              {item.creative && <p className="mt-1 text-sm text-muted-foreground">Criativo: {item.creative.title}</p>}
            </div>

            {item.caption && (
              <div className="whitespace-pre-wrap rounded-2xl border bg-muted/30 p-3 text-sm leading-6 text-foreground">
                {item.caption}
              </div>
            )}

            {item.cta && <p className="text-sm font-semibold text-foreground">CTA: {item.cta}</p>}

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {text && (
                <Button type="button" variant="secondary" className="gap-2" onClick={() => onCopy(text)}>
                  <Clipboard className="h-4 w-4" />
                  Copiar legenda + CTA
                </Button>
              )}

              {item.creative?.fileUrl && (
                <Button asChild type="button" variant="outline" className="gap-2">
                  <a href={item.creative.fileUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4" />
                    Abrir criativo
                  </a>
                </Button>
              )}

              {!item.published && (
                <Button type="button" className="gap-2" onClick={() => onPublished(item)}>
                  <CheckCircle2 className="h-4 w-4" />
                  Marcar como publicado
                </Button>
              )}

              <DangerButton
                label="Excluir publicação"
                confirmMessage="Excluir esta publicação do planner?"
                onConfirm={() => onDelete(item.deleteUrl, "Publicação excluída")}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TextInput({ label, name, type = "text", required = false }: { label: string; name: string; type?: string; required?: boolean }) {
  return (
    <label className="block space-y-1.5 text-sm font-bold text-foreground">
      <span>{label}</span>
      <input
        name={name}
        type={type}
        required={required}
        className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-base outline-none transition focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}

function TextArea({ label, name, rows = 3 }: { label: string; name: string; rows?: number }) {
  return (
    <label className="block space-y-1.5 text-sm font-bold text-foreground">
      <span>{label}</span>
      <textarea
        name={name}
        rows={rows}
        className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-base outline-none transition focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}

export default function AnfitriaoMarketing() {
  const [marketing, setMarketing] = useState<HostMarketingPayload | null>(null);
  const [dashboard, setDashboard] = useState<AnfitriaoDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      fetch("/anfitriao/marketing/data", { credentials: "same-origin", headers: { Accept: "application/json" } }).then((res) => {
        if (!res.ok) throw new Error("Erro ao carregar marketing");
        return res.json();
      }),
      fetch("/anfitriao/dashboard/data", { credentials: "same-origin", headers: { Accept: "application/json" } }).then((res) => {
        if (!res.ok) throw new Error("Erro ao carregar dashboard");
        return res.json();
      }),
    ])
      .then(([marketingData, dashboardData]) => {
        setMarketing(marketingData);
        setDashboard(dashboardData);
      })
      .finally(() => setLoading(false));
  }, []);

  const campaigns = useMemo(() => {
    if (!marketing) return [];
    const orphanItems = marketing.plannerItems.filter((item) => !item.campaignId);
    const list = [...marketing.campaigns];
    if (orphanItems.length) {
      list.push({ id: 0, title: "Sem campanha", description: "Publicações ainda não vinculadas a uma campanha." });
    }
    return list;
  }, [marketing]);

  async function copyText(text: string) {
    await navigator.clipboard.writeText(text);
    setToast("Legenda + CTA copiados");
    window.setTimeout(() => setToast(null), 1800);
  }

  async function postAction(url: string, successMessage: string) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-TOKEN": getCsrfToken(),
        "X-Requested-With": "XMLHttpRequest",
        Accept: "text/html,application/xhtml+xml,application/xml",
      },
      credentials: "same-origin",
    });

    if (response.ok) {
      setToast(successMessage);
      window.setTimeout(() => window.location.reload(), 500);
      return;
    }

    setToast("Não foi possível concluir a ação");
    window.setTimeout(() => setToast(null), 2200);
  }

  async function markPublished(item: PlannerItem) {
    await postAction(item.markPublishedUrl, "Publicação marcada como publicada");
  }

  if (loading) {
    return <div className="p-6">Carregando marketing...</div>;
  }

  if (!marketing || !dashboard) {
    return <div className="p-6">Não foi possível carregar os dados do marketing.</div>;
  }

  const firstName = dashboard.user.name?.split(" ")[0] || marketing.hostName || "Anfitrião";
  const cidadeEstado = [dashboard.user.cidade, dashboard.user.estado].filter(Boolean).join(" - ") || "Sua região";
  const perfilPct = Math.max(0, Math.min(100, dashboard.profile.perfil_pct || 0));

  return (
    <>
      <Header activeTab="anfitrioes" isCompact hideAudienceNav />

      <div className="min-h-screen gradient-hero">
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 md:py-8">
          <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
            <aside className="w-full lg:w-72 shrink-0">
              <ProfileSidebarAnfitriao
                userName={firstName}
                bio={dashboard.profile.bio || ""}
                avatarUrl={dashboard.profile.avatar_url || ""}
                perfilPct={perfilPct}
                cidadeEstado={cidadeEstado}
                links={dashboard.links}
                isMv={dashboard.summary.is_mv}
                profileItems={dashboard.profile.profileItems || dashboard.profile.profile_items || []}
                abaAtiva="marketing"
              />
            </aside>

            <main className="flex-1 min-w-0 space-y-5">
              <section className="overflow-hidden rounded-3xl gradient-primary text-white card-shadow">
                <div className="p-5 sm:p-6 md:p-8">
                  <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                    <div className="max-w-2xl">
                      <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wide">
                        <Megaphone className="h-3.5 w-3.5" />
                        Marketing dos Anfitriões
                      </div>
                      <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight">Meu Marketing</h1>
                      <p className="mt-2 max-w-xl text-sm sm:text-base text-white/85">
                        Execute suas publicações com o criativo certo, copie legenda e CTA juntos e acompanhe o que ainda falta postar.
                      </p>
                    </div>
                    <div className="rounded-3xl bg-white/15 p-4 text-sm font-semibold text-white/90">
                      Pensado para execução rápida no celular.
                    </div>
                  </div>
                </div>
              </section>

              <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Card className="card-shadow border-0"><CardContent className="p-4"><p className="text-sm font-semibold text-muted-foreground">Campanha ativa</p><p className="mt-1 text-3xl font-black text-foreground">{marketing.stats.activeCampaignTotal}</p></CardContent></Card>
                <Card className="card-shadow border-0"><CardContent className="p-4"><p className="text-sm font-semibold text-muted-foreground">Pendentes</p><p className="mt-1 text-3xl font-black text-foreground">{marketing.stats.pendingCount}</p></CardContent></Card>
                <Card className="card-shadow border-0"><CardContent className="p-4"><p className="text-sm font-semibold text-muted-foreground">Publicadas</p><p className="mt-1 text-3xl font-black text-foreground">{marketing.stats.publishedCount}</p></CardContent></Card>
              </section>

              {marketing.nextPlannerItem ? (
                <Card className="card-shadow border-0">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Pill tone="green">Próxima publicação</Pill>
                    </div>
                    <CardTitle className="text-xl font-black">Prioridade para executar</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ItemCard item={marketing.nextPlannerItem} onCopy={copyText} onPublished={markPublished} onDelete={postAction} featured />
                  </CardContent>
                </Card>
              ) : (
                <Card className="card-shadow border-0"><CardContent className="p-5"><h2 className="text-xl font-black">Nenhuma publicação pendente</h2><p className="mt-1 text-muted-foreground">Não há itens pendentes no momento.</p></CardContent></Card>
              )}

              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.55fr)_minmax(320px,0.85fr)]">
                <section className="space-y-4">
                  <Card className="card-shadow border-0">
                    <CardContent className="p-5">
                      <h2 className="text-xl font-black text-foreground">Campanhas</h2>
                      <p className="mt-1 text-sm text-muted-foreground">Abra a campanha e depois a semana que deseja executar.</p>
                    </CardContent>
                  </Card>

                  {campaigns.length ? campaigns.map((campaign, index) => {
                    const items = marketing.plannerItems.filter((item) => campaign.id ? item.campaignId === campaign.id : !item.campaignId);
                    const weeks = normalizeWeek(items);
                    const deleteAction = campaign.deleteUrl ? (
                      <DangerButton
                        label="Excluir"
                        confirmMessage="Excluir esta campanha da área de marketing?"
                        onConfirm={() => postAction(campaign.deleteUrl!, "Campanha excluída")}
                      />
                    ) : null;

                    return (
                      <Collapsible key={campaign.id} title={campaign.title} count={items.length} defaultOpen={index === 0} action={deleteAction}>
                        <div className="space-y-4">
                          {campaign.description && <p className="text-sm text-muted-foreground">{campaign.description}</p>}
                          {[...weeks.entries()].length ? [...weeks.entries()].map(([weekNumber, weekItems], weekIndex) => (
                            <Collapsible key={weekNumber} title={weekNumber ? `Semana ${weekNumber}` : "Sem semana definida"} count={weekItems.length} defaultOpen={weekIndex === 0}>
                              <div className="space-y-3">
                                {weekItems.map((item) => <ItemCard key={item.id} item={item} onCopy={copyText} onPublished={markPublished} onDelete={postAction} />)}
                              </div>
                            </Collapsible>
                          )) : <p className="text-sm text-muted-foreground">Nenhuma publicação nesta campanha.</p>}
                        </div>
                      </Collapsible>
                    );
                  }) : <Card className="card-shadow border-0"><CardContent className="p-5 text-muted-foreground">Nenhuma campanha disponível.</CardContent></Card>}
                </section>

                <aside className="space-y-4">
                  <Collapsible title="Meus criativos" count={marketing.creatives.length} defaultOpen>
                    <div className="space-y-3">
                      {marketing.creatives.length ? marketing.creatives.map((creative) => (
                        <Card key={creative.id} className="border shadow-sm">
                          <CardContent className="space-y-3 p-4">
                            <div className="flex flex-wrap gap-2">
                              <Pill>{creative.source === "admin" ? "AllePets" : "Meu criativo"}</Pill>
                              <Pill tone={creative.status === "approved" ? "green" : "default"}>{creative.statusLabel}</Pill>
                            </div>
                            <h3 className="font-black leading-tight text-foreground">{creative.title}</h3>
                            {creative.fileUrl && creative.isImage && (
                              <a href={creative.fileUrl} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-2xl border bg-muted/20">
                                <img src={creative.fileUrl} alt={creative.title} className="h-auto max-h-72 w-full object-contain" />
                              </a>
                            )}
                            <div className="flex flex-col gap-2">
                              {creative.fileUrl && <Button asChild variant="outline"><a href={creative.fileUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" />Abrir arquivo</a></Button>}
                              {creative.duplicateUrl && (
                                <form method="POST" action={creative.duplicateUrl}>
                                  <input type="hidden" name="_token" value={getCsrfToken()} />
                                  <Button type="submit" variant="secondary" className="w-full">Usar como modelo</Button>
                                </form>
                              )}
                              {creative.canDelete && creative.deleteUrl && (
                                <DangerButton
                                  label="Excluir criativo"
                                  confirmMessage="Excluir este criativo? Publicações que usam este criativo ficarão sem arquivo vinculado."
                                  onConfirm={() => postAction(creative.deleteUrl!, "Criativo excluído")}
                                />
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      )) : <p className="text-sm text-muted-foreground">Nenhum criativo disponível.</p>}
                    </div>
                  </Collapsible>

                  <Collapsible title="Enviar meu próprio criativo">
                    <form className="space-y-3" method="POST" action={marketing.routes.storeCreative} encType="multipart/form-data">
                      <input type="hidden" name="_token" value={getCsrfToken()} />
                      <TextInput label="Título" name="title" required />
                      <TextInput label="Arquivo" name="file" type="file" />
                      <TextInput label="Canal" name="channel" />
                      <TextInput label="Formato" name="format" />
                      <TextInput label="CTA" name="cta" />
                      <TextArea label="Descrição" name="description" rows={2} />
                      <TextArea label="Legenda modelo" name="caption_template" rows={3} />
                      <Button type="submit" className="w-full gap-2"><UploadCloud className="h-4 w-4" />Enviar para aprovação</Button>
                    </form>
                  </Collapsible>

                  <Collapsible title="Criar publicação própria">
                    <form className="space-y-3" method="POST" action={marketing.routes.storePlannerItem}>
                      <input type="hidden" name="_token" value={getCsrfToken()} />
                      <label className="block space-y-1.5 text-sm font-bold text-foreground">
                        <span>Dia da semana</span>
                        <select name="day_label" className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-base outline-none transition focus:ring-2 focus:ring-primary/20">
                          <option value="">Selecione</option>
                          {marketing.dayOptions.map((day) => <option key={day} value={day}>{day}</option>)}
                        </select>
                      </label>
                      <label className="block space-y-1.5 text-sm font-bold text-foreground">
                        <span>Criativo</span>
                        <select name="creative_id" className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-base outline-none transition focus:ring-2 focus:ring-primary/20">
                          <option value="">Sem criativo</option>
                          {marketing.creatives.map((creative) => <option key={creative.id} value={creative.id}>{creative.title}</option>)}
                        </select>
                      </label>
                      <TextInput label="Canal" name="channel" />
                      <TextInput label="Formato" name="format" />
                      <TextInput label="Tema" name="theme" />
                      <TextArea label="Legenda" name="caption" rows={4} />
                      <TextInput label="CTA" name="cta" />
                      <TextInput label="Observações" name="notes" />
                      <Button type="submit" className="w-full gap-2"><Send className="h-4 w-4" />Enviar para aprovação</Button>
                    </form>
                  </Collapsible>
                </aside>
              </div>
            </main>
          </div>
        </div>
      </div>

      {toast && <div className="fixed bottom-4 left-4 right-4 z-50 rounded-full bg-foreground px-4 py-3 text-center text-sm font-bold text-background shadow-lg sm:left-auto sm:right-6 sm:w-auto">{toast}</div>}
    </>
  );
}
